import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import api from "../utils/api";
import { formatPrice } from "../utils/helpers";
import { getSelectedOptionsKey, getSelectedOptionsList } from "../utils/productOptions";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [visibleCategories, setVisibleCategories] = useState([]);
  const location = useLocation();
  const { isAuthenticated, isAdmin, isApproved, logout } = useAuth();
  const { cart, updateQuantity, removeFromCart, getCartTotal, getCartCount } = useCart();
  const { selectedCurrency, setSelectedCurrency, supportedCurrencies, exchangeRates } = useCurrency();

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const { data } = await api.get("/categories");
        setVisibleCategories(data.categories || []);
      } catch (error) {
        console.error("Error loading header categories:", error);
      }
    };

    loadCategories();
  }, [location.pathname]);

  useEffect(() => {
    setCategoryMenuOpen(false);
    setUserMenuOpen(false);
    setCartDrawerOpen(false);
  }, [location.pathname, location.search]);

  const categoryNavigation = useMemo(
    () =>
      visibleCategories.map((category) => ({
        name: category.name,
        path: `/products?category=${encodeURIComponent(category.slug)}`,
        match: ["/products"],
        categorySlug: category.slug,
      })),
    [visibleCategories],
  );

  const directCategoryNavigation = categoryNavigation.length > 4 ? categoryNavigation.slice(0, 3) : categoryNavigation;
  const overflowCategoryNavigation = categoryNavigation.length > 4 ? categoryNavigation.slice(3) : [];
  const navigation = directCategoryNavigation;

  const isPathActive = (item) => {
    if (item.path === "/") {
      return location.pathname === "/";
    }

    if (item.categorySlug) {
      return location.pathname === "/products" && new URLSearchParams(location.search).get("category") === item.categorySlug;
    }

    return (item.match || [item.path]).some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  };

  const userLinks = isAuthenticated()
    ? [
        ...(isAdmin()
          ? [{ name: "Admin", path: "/admin", match: ["/admin"] }]
          : [
              { name: "Account", path: "/account", match: ["/account"] },
              ...(isApproved() ? [{ name: "Orders", path: "/orders", match: ["/orders"] }] : []),
            ]),
      ]
    : [{ name: "Trade Login", path: "/login", match: ["/login"] }];

  const unauthDesktopLinkClass = "text-xs font-medium tracking-[0.06em] text-white transition-colors duration-200 hover:text-gold-100";
  const unauthMobileLinkClass = "mb-2 block text-sm font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-200 hover:text-gold-100";

  const cartButton = isAuthenticated() && !isAdmin() ? (
    <button
      type="button"
      aria-label="Open cart"
      aria-expanded={cartDrawerOpen}
      onClick={() => setCartDrawerOpen(true)}
      className={`relative rounded-md p-2 transition-all duration-200 ${
        isPathActive({ path: "/cart", match: ["/cart"] })
          ? "bg-metallic-gold text-black shadow-gold-metal"
          : "text-white hover:bg-gold-500/10 hover:text-gold-100"
      }`}
    >
      <svg className="w-6 h-6 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
      {getCartCount() > 0 && <span className="absolute -top-2 -right-2 bg-gold-500 text-black text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">{getCartCount()}</span>}
    </button>
  ) : null;

  return (
    <>
    <header className="sticky top-0 z-50 border-b border-gold-900/35 bg-[linear-gradient(180deg,rgba(18,18,18,0.94)_0%,rgba(0,0,0,0.88)_100%)] backdrop-blur-xl">
      <nav className="container mx-auto px-3 py-2 sm:px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <motion.div whileHover={{ scale: 1.03 }} className="flex items-center">
              <img src="/logo.png" alt="Tabram Eyewear" className="h-16 w-auto object-contain sm:h-20 lg:h-24" />
              <span className="sr-only">Tabram Eyewear</span>
            </motion.div>
          </Link>

          <div className="flex items-center gap-2 sm:gap-4">
            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-2 px-4">
              <div className="flex items-center gap-2">
                {navigation.map((item) => {
                  const active = isPathActive(item);

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`relative rounded-md px-4 py-2 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                        active
                          ? "bg-metallic-gold text-black shadow-gold-metal"
                          : "text-gray-200 hover:text-gold-100 hover:bg-gold-500/10"
                      }`}
                    >
                      <span className="relative z-10">{item.name}</span>
                    </Link>
                  );
                })}

                {overflowCategoryNavigation.length > 0 && (
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setCategoryMenuOpen((current) => !current)}
                      className={`relative rounded-md px-4 py-2 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                        overflowCategoryNavigation.some((item) => isPathActive(item))
                          ? "bg-metallic-gold text-black shadow-gold-metal"
                          : "text-gray-200 hover:text-gold-100 hover:bg-gold-500/10"
                      }`}
                    >
                      More
                    </button>
                    {categoryMenuOpen && (
                      <div className="absolute left-0 top-full mt-2 min-w-[14rem] rounded-xl border border-gold-200/10 bg-[linear-gradient(180deg,rgba(22,18,10,0.92)_0%,rgba(6,6,6,0.98)_100%)] p-2 shadow-[0_16px_36px_rgba(0,0,0,0.42)]">
                        {overflowCategoryNavigation.map((item) => (
                          <Link
                            key={item.categorySlug}
                            to={item.path}
                            className={`mb-1 block rounded-md px-4 py-3 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                              isPathActive(item)
                                ? "bg-metallic-gold text-black shadow-gold-metal"
                                : "text-white hover:bg-gold-500/10 hover:text-gold-100"
                            }`}
                            onClick={() => setCategoryMenuOpen(false)}
                          >
                            {item.name}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="mx-3 h-8 w-px bg-gold-900/35" />

              <div className="flex items-center gap-2">
                {isAuthenticated() ? (
                  <>
                    {isAdmin() ? (
                      <>
                        {userLinks.map((item) => {
                          const active = isPathActive(item);

                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              className={`rounded-md px-4 py-2 text-sm font-medium transition-all duration-200 ${
                                active
                                  ? "bg-metallic-gold text-black shadow-gold-metal"
                                  : "text-white hover:bg-gold-500/10 hover:text-gold-100"
                              }`}
                            >
                              {item.name}
                            </Link>
                          );
                        })}
                        <button onClick={logout} className="rounded-md px-4 py-2 text-sm font-medium text-white transition-all duration-200 hover:bg-gold-500/10 hover:text-gold-100">
                          Logout
                        </button>
                      </>
                    ) : (
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setUserMenuOpen((current) => !current)}
                          className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-700 text-white transition-all duration-200 hover:border-gold-300/30 hover:bg-gold-500/10 hover:text-gold-100"
                          aria-label="Open account menu"
                          aria-expanded={userMenuOpen}
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zm-10.5 13.5a6.75 6.75 0 1113.5 0" />
                          </svg>
                        </button>
                        {userMenuOpen && (
                          <div className="absolute right-0 top-full mt-3 min-w-[12rem] rounded-xl border border-gold-200/10 bg-[linear-gradient(180deg,rgba(22,18,10,0.92)_0%,rgba(6,6,6,0.98)_100%)] p-2 shadow-[0_16px_36px_rgba(0,0,0,0.42)]">
                            {userLinks.map((item) => (
                              <Link
                                key={item.path}
                                to={item.path}
                                className="mb-1 block rounded-md px-4 py-3 text-sm font-semibold tracking-[0.08em] uppercase text-white transition-all duration-200 hover:bg-gold-500/10 hover:text-gold-100"
                                onClick={() => setUserMenuOpen(false)}
                              >
                                {item.name}
                              </Link>
                            ))}
                            <button
                              onClick={() => {
                                logout();
                                setUserMenuOpen(false);
                              }}
                              className="block w-full rounded-md px-4 py-3 text-left text-sm font-semibold tracking-[0.08em] uppercase text-white transition-all duration-200 hover:bg-gold-500/10 hover:text-gold-100"
                            >
                              Logout
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex items-center gap-4">
                    {userLinks.map((item) => (
                      <Link key={item.path} to={item.path} className={unauthDesktopLinkClass}>
                        {item.name}
                      </Link>
                    ))}
                    <Link to="/register" className={unauthDesktopLinkClass}>
                      Create Account
                    </Link>
                  </div>
                )}

                {isAuthenticated() && supportedCurrencies.length > 0 && (
                  <select
                    value={selectedCurrency}
                    onChange={(e) => setSelectedCurrency(e.target.value)}
                    className="rounded-md border border-gray-700 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500"
                    aria-label="Select display currency"
                  >
                    {supportedCurrencies.map((currency) => (
                      <option key={currency} value={currency}>
                        {currency}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            {cartButton}

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="rounded-md border border-gold-200/10 bg-[linear-gradient(135deg,rgba(255,243,178,0.05)_0%,rgba(212,175,55,0.08)_100%)] p-2 text-white transition-all duration-200 hover:border-gold-200/25 hover:text-gold-100 lg:hidden">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="mt-4 overflow-hidden rounded-3xl border border-gold-200/10 bg-[linear-gradient(180deg,rgba(22,18,10,0.88)_0%,rgba(6,6,6,0.96)_100%)] px-4 py-4 shadow-[0_16px_36px_rgba(0,0,0,0.42),inset_0_1px_0_rgba(255,248,221,0.08)] lg:hidden">
            {navigation.map((item) => (
              <Link
                key={item.categorySlug || item.path}
                to={item.path}
                className={`mb-2 block rounded-md px-4 py-3 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                  isPathActive(item)
                    ? "bg-metallic-gold text-black shadow-gold-metal"
                    : "text-white hover:bg-gold-500/10 hover:text-gold-100"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {overflowCategoryNavigation.map((item) => (
              <Link
                key={item.categorySlug}
                to={item.path}
                className={`mb-2 block rounded-md px-4 py-3 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                  isPathActive(item)
                    ? "bg-metallic-gold text-black shadow-gold-metal"
                    : "text-white hover:bg-gold-500/10 hover:text-gold-100"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            {isAuthenticated() ? (
              <>
                {userLinks.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`mb-2 block rounded-md px-4 py-3 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                      isPathActive(item)
                        ? "bg-metallic-gold text-black shadow-gold-metal"
                        : "text-white hover:bg-gold-500/10 hover:text-gold-100"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                {!isAdmin() && (
                  <Link
                    to="/cart"
                    className={`mb-2 block rounded-md px-4 py-3 text-sm font-semibold tracking-[0.08em] uppercase transition-all duration-200 ${
                      isPathActive({ path: "/cart", match: ["/cart"] })
                        ? "bg-metallic-gold text-black shadow-gold-metal"
                        : "text-white hover:bg-gold-500/10 hover:text-gold-100"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Cart
                  </Link>
                )}
                <button
                  onClick={() => {
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="mt-2 block w-full rounded-md px-4 py-3 text-left text-sm font-semibold tracking-[0.08em] uppercase text-white transition-all duration-200 hover:bg-gold-500/10 hover:text-gold-100"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                {userLinks.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={unauthMobileLinkClass}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link to="/register" className="mt-2 block text-sm font-semibold uppercase tracking-[0.08em] text-white transition-colors duration-200 hover:text-gold-100" onClick={() => setMobileMenuOpen(false)}>
                  Create Account
                </Link>
              </>
            )}
            {isAuthenticated() && supportedCurrencies.length > 0 && (
              <div className="mt-4 border-t border-gold-900/35 pt-4">
                <label className="mb-2 block text-sm text-gray-400">Display Currency</label>
                <select
                  value={selectedCurrency}
                  onChange={(e) => setSelectedCurrency(e.target.value)}
                  className="w-full rounded-md border border-gray-700 bg-black px-3 py-2 text-sm text-white focus:outline-none focus:border-gold-500"
                  aria-label="Select display currency"
                >
                  {supportedCurrencies.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </motion.div>
        )}
      </nav>
    </header>

    {cartDrawerOpen && isAuthenticated() && !isAdmin() && (
      <div className="fixed inset-0 z-[60]">
        <button type="button" aria-label="Close cart drawer" className="absolute inset-0 bg-black/60" onClick={() => setCartDrawerOpen(false)} />
        <aside className="absolute right-0 top-0 h-full w-full max-w-md border-l border-gold-200/10 bg-[linear-gradient(180deg,rgba(22,18,10,0.96)_0%,rgba(6,6,6,0.98)_100%)] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.6)]" role="dialog" aria-modal="true" aria-label="Cart drawer">
          <div className="mb-5 flex items-center justify-between border-b border-gold-900/30 pb-4">
            <h2 className="heading-metallic text-2xl">Shopping Cart</h2>
            <button type="button" onClick={() => setCartDrawerOpen(false)} className="rounded-md border border-gold-200/10 p-2 text-white hover:text-gold-100" aria-label="Close cart">
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="flex h-[calc(100%-7rem)] flex-col items-center justify-center text-center">
              <p className="text-lg font-semibold text-white">Your cart is empty</p>
              <p className="mt-2 text-sm text-gray-400">Add something to keep shopping.</p>
              <Link to="/products" onClick={() => setCartDrawerOpen(false)} className="btn-primary mt-6">Browse Products</Link>
            </div>
          ) : (
            <div className="flex h-[calc(100%-9rem)] flex-col">
              <div className="flex-1 space-y-4 overflow-y-auto pr-1">
                {cart.map((item) => (
                  <div key={item.id ?? `${item.product_id}-${getSelectedOptionsKey(item.selected_options)}`} className="rounded-2xl border border-gold-900/30 bg-black/20 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-white">{item.product?.name || item.product_name || "Product"}</p>
                        {getSelectedOptionsList(item.selected_options).length > 0 && (
                          <div className="mt-1 text-xs text-gray-400">
                            {getSelectedOptionsList(item.selected_options).map((option) => (
                              <p key={`${option.name}-${option.value}`}>
                                {option.name}: {option.value}
                              </p>
                            ))}
                          </div>
                        )}
                      </div>
                      <button type="button" onClick={() => removeFromCart(item)} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateQuantity(item, Number.parseInt(e.target.value, 10))}
                        className="input-field w-20 text-center"
                        aria-label={`Quantity for ${item.product?.name || item.product_name || "item"}`}
                      />
                      <span className="font-semibold text-gold-500">
                        {item.product?.price ? formatPrice(item.product.price * item.quantity, selectedCurrency, exchangeRates) : "Login to view"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 border-t border-gold-900/30 pt-4">
                <div className="mb-4 flex items-center justify-between text-sm text-gray-300">
                  <span>Items ({getCartCount()})</span>
                  <span>{formatPrice(getCartTotal(), selectedCurrency, exchangeRates)}</span>
                </div>
                <div className="space-y-3">
                  <Link to="/cart" onClick={() => setCartDrawerOpen(false)} className="btn-primary block text-center">View Cart</Link>
                  <Link to="/checkout" onClick={() => setCartDrawerOpen(false)} className="block rounded-lg border border-gold-300/25 bg-gold-500/10 px-4 py-3 text-center font-semibold text-gold-100 hover:bg-gold-500/15">
                    Checkout
                  </Link>
                </div>
              </div>
            </div>
          )}
        </aside>
      </div>
    )}
    </>
  );
};

export default Header;
